import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Disc3 } from "lucide-react";
import { useApi, usePagedList } from "@/lib/api";
import { EventCard } from "@/components/site/EventCard";
import { ClubWeekend } from "@/components/site/ClubWeekend";
import { Container, LoadingGrid, PageHeader } from "@/components/site/PageHeader";
import { LoadMore } from "@/components/site/ListControls";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EVENT_TYPES, isEventType } from "@/lib/eventTypes";
import { WeekPosterDialog } from "@/components/site/WeekPosterDialog";

const chipCls = (on) => `h-10 px-4 border text-[11px] uppercase tracking-[0.2em] font-bold transition-colors duration-200 ${on ? "bg-white text-black border-white" : "border-white/25 text-zinc-200 hover:border-white hover:text-white"}`;

const TypeFilter = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-2" role="group" aria-label="Catégorie d'événement" data-testid="agenda-type-filter">
    {[{ key: "all", label: "Tout" }, ...EVENT_TYPES].map((t) => (
      <button key={t.key} type="button" onClick={() => onChange(t.key)} aria-pressed={value === t.key} data-testid={`agenda-type-${t.key}`} className={chipCls(value === t.key)}>
        {t.label}
      </button>
    ))}
  </div>
);

const tabCls =
  "rounded-none font-display uppercase text-lg px-6 py-3 text-zinc-400 data-[state=active]:bg-white data-[state=active]:text-black";

export default function Agenda() {
  const [when, setWhen] = useState("upcoming");
  const [city, setCity] = useState("all");
  const [club, setClub] = useState(false);
  const [params, setParams] = useSearchParams();
  const type = isEventType(params.get("type")) ? params.get("type") : "all";
  // the chosen category lives in the address (lgtdz.com/agenda?type=danse) so it can be shared
  const setType = (t) => setParams((p) => { const n = new URLSearchParams(p); if (t === "all") n.delete("type"); else n.set("type", t); return n; }, { replace: true });
  const { data: cities = [] } = useApi("/events-cities");
  const list = usePagedList("/events", { when, city: city === "all" ? undefined : city, type: type === "all" ? undefined : type }, 24);
  const data = list.items;
  const isLoading = list.isLoading;

  return (
    <>
      <PageHeader
        top="Agenda"
        bottom="événements"
        intro="Concerts, soirées club, festivals, battles de danse, spectacles et conférences : les rendez-vous LGTDZ en France et en Europe."
        testId="agenda-header"
      />
      <Container className="py-12 flex flex-col md:flex-row gap-6 md:items-center justify-between border-b border-white/10">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
          <Tabs value={club ? "" : when} onValueChange={(v) => { setClub(false); setWhen(v); }}>
            <TabsList className="rounded-none bg-transparent border border-white/20 p-0 h-auto">
              <TabsTrigger value="upcoming" className={tabCls} data-testid="agenda-tab-upcoming">À venir</TabsTrigger>
              <TabsTrigger value="past" className={tabCls} data-testid="agenda-tab-past">Passés</TabsTrigger>
              <TabsTrigger value="all" className={tabCls} data-testid="agenda-tab-all">Tous</TabsTrigger>
            </TabsList>
          </Tabs>
          <button type="button" onClick={() => setClub((v) => !v)} aria-pressed={club} data-testid="agenda-club-weekend-btn"
            className={`inline-flex items-center justify-center gap-2 h-[52px] px-5 border font-display uppercase text-base transition-colors duration-200 ${club ? "bg-primary border-primary text-white" : "border-white/20 text-white hover:border-primary hover:text-primary"}`}>
            <Disc3 className={`w-5 h-5 ${club ? "animate-spin" : ""}`} /> Ce week-end en club
          </button>
        </div>
        {!club && <Select value={city} onValueChange={setCity}>
          <SelectTrigger className="w-full md:w-64 rounded-none border-white/20 bg-transparent h-12 uppercase tracking-[0.15em] text-xs font-bold" data-testid="agenda-city-select">
            <SelectValue placeholder="Ville" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-black border-white/10">
            <SelectItem value="all" data-testid="agenda-city-all">Toutes les villes</SelectItem>
            {cities.map((c) => (
              <SelectItem key={c} value={c} data-testid={`agenda-city-${c}`}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>}
      </Container>
      {!club && (
        <Container className="pt-8 flex flex-wrap items-center justify-between gap-4">
          <TypeFilter value={type} onChange={setType} />
          <WeekPosterDialog key={type} initialType={type} label="Affiche de la semaine" testId="agenda-week-poster-btn"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-white underline decoration-primary decoration-2 underline-offset-4 hover:text-primary transition-colors" />
        </Container>
      )}
      <Container className="py-20">
        {club ? <ClubWeekend /> : isLoading ? (
          <LoadingGrid />
        ) : data.length === 0 ? (
          <div className="py-20" data-testid="agenda-empty">
            <p className="font-display uppercase text-5xl md:text-7xl text-white">Rien de prévu<span className="text-primary">.</span></p>
            <p className="text-zinc-400 mt-4 max-w-lg">
              Pas de date annoncée pour l'instant{type !== "all" ? " dans cette catégorie" : ""}. Inscris-toi à la newsletter pour être prévenu·e des prochains événements, ou consulte les événements passés.
            </p>
            <button onClick={() => setWhen("past")} data-testid="agenda-see-past-btn" className="mt-8 border-2 border-white text-white font-display uppercase text-xl px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200">
              Voir les événements passés
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16" data-testid="agenda-grid">
            {data.map((e, i) => <EventCard key={e.id} event={e} index={i % 24} />)}
          </div>
        )}
        {!club && <LoadMore list={list} testId="agenda-load-more" />}
      </Container>
    </>
  );
}
